import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/components/providers/AuthProvider";

interface Product {
  id: number;
  name: string;
  code: string;
  unit: string;
  category_id: number;
  quantity: number;
}

interface Department {
  id: number;
  name: string;
  acronym: string;
}

interface Category {
  id: number;
  name: string;
}

interface Construction {
  id: number;
  name: string;
  department_id: number;
  status: string;
  address: string;
}

interface OrderProduct {
  product_id: number;
  quantity: number;
  product_name: string;
  product_code: string;
  product_unit: string;
}

interface Order {
  department_id: number;
  construction_name: string;
  products: OrderProduct[];
  responsible_name: string;
  status: 'pending' | 'approved' | 'rejected';
  user_id: string | undefined;
}

const CreateOrder = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [constructions, setConstructions] = useState<Construction[]>([]);
  const [userDepartment, setUserDepartment] = useState<Department | null>(null);
  
  // Form state
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedConstruction, setSelectedConstruction] = useState("");
  const [selectedProducts, setSelectedProducts] = useState<OrderProduct[]>([]);
  const [responsibleName, setResponsibleName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [quantity, setQuantity] = useState<number>(1);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    // Carregar dados iniciais
    const loadInitialData = async () => {
      setLoading(true);
      try {
        await loadUserDepartment();
        await fetchCategories();
      } catch (error) {
        console.error('Erro ao carregar dados iniciais:', error);
        toast({
          title: "Erro",
          description: "Não foi possível carregar os dados necessários. Por favor, tente novamente.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [user]);

  const loadUserDepartment = async () => {
    try {
      if (!user) return;

      console.log('Buscando departamento do usuário...');

      // Buscar o perfil do usuário
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select(`
          department_id,
          departments (
            id,
            name,
            acronym
          )
        `)
        .eq('id', user.id)
        .single();

      if (profileError) {
        console.error('Erro ao buscar perfil:', profileError);
        throw profileError;
      }

      console.log('Perfil encontrado:', profile);

      if (profile?.departments) {
        const department = {
          id: profile.departments.id,
          name: profile.departments.name,
          acronym: profile.departments.acronym
        };

        setUserDepartment(department);
        setSelectedDepartment(department);
        setDepartments([department]);
      } else {
        toast({
          title: "Erro",
          description: "Você precisa ter uma secretaria definida no seu perfil para criar pedidos.",
          variant: "destructive",
        });
        navigate('/user/settings');
      }

    } catch (error) {
      console.error('Erro ao carregar departamento do usuário:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar seu departamento. Por favor, tente novamente.",
        variant: "destructive",
      });
    }
  };

  const fetchDepartments = async () => {
    try {
      const { data, error } = await supabase
        .from('departments')
        .select('id, name, acronym')
        .order('acronym');

      if (error) {
        console.error('Erro ao carregar departamentos:', error);
        throw error;
      }

      console.log('Departamentos carregados:', data);
      setDepartments(data || []);
    } catch (error) {
      console.error('Erro ao carregar departamentos:', error);
      toast({
        title: "Erro ao carregar departamentos",
        description: "Não foi possível carregar a lista de secretarias",
        variant: "destructive",
      });
    }
  };

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('id, name')
        .order('name');

      if (error) throw error;

      setCategories(data || []);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar a lista de categorias",
        variant: "destructive",
      });
    }
  };

  const fetchConstructions = async () => {
    try {
      const { data, error } = await supabase
        .from('constructions')
        .select('*')
        .eq('department_id', selectedDepartment?.id)
        .eq('status', 'Em Andamento')
        .order('name');

      if (error) throw error;

      setConstructions(data || []);
    } catch (error) {
      console.error('Erro ao buscar obras:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar a lista de obras.",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    if (selectedDepartment) {
      fetchConstructions();
    } else {
      setConstructions([]);
    }
  }, [selectedDepartment]);

  const fetchProducts = async (departmentId: number, categoryId: number | null = null) => {
    try {
      console.log('Buscando produtos para secretaria:', departmentId, 'e categoria:', categoryId);
      
      let query = supabase
        .from('products')
        .select(`
          id,
          name,
          code,
          unit,
          category_id,
          inventory!inner (
            quantity,
            department_id
          )
        `)
        .eq('inventory.department_id', departmentId);

      if (categoryId) {
        query = query.eq('category_id', categoryId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Erro na query:', error);
        throw error;
      }

      const formattedProducts = data?.map(product => ({
        id: product.id,
        name: product.name,
        code: product.code,
        unit: product.unit,
        department_id: product.inventory[0].department_id,
        quantity: product.inventory[0].quantity,
        category_id: product.category_id
      })) || [];

      console.log('Produtos encontrados:', formattedProducts);
      setProducts(formattedProducts);
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar a lista de produtos",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    if (selectedDepartment && selectedCategory) {
      console.log('Secretaria e categoria selecionadas:', selectedDepartment, selectedCategory);
      fetchProducts(selectedDepartment.id, selectedCategory);
    } else {
      setProducts([]);
    }
  }, [selectedDepartment, selectedCategory]);

  const handleAddProduct = () => {
    if (!selectedProduct) return;

    // Verificar se o produto já foi adicionado
    const existingProduct = selectedProducts.find(p => p.product_id === selectedProduct.id);
    if (existingProduct) {
      toast({
        title: "Atenção",
        description: "Este produto já foi adicionado ao pedido.",
        variant: "destructive",
      });
      return;
    }

    // Adicionar o produto à lista com todas as informações necessárias
    setSelectedProducts(prev => [...prev, {
      product_id: selectedProduct.id,
      quantity: quantity,
      product_name: selectedProduct.name,
      product_code: selectedProduct.code,
      product_unit: selectedProduct.unit
    }]);

    // Limpar os campos
    setSelectedProduct(null);
    setQuantity(1);
  };

  const handleQuantityChange = (value: string) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue > 0) {
      setQuantity(numValue);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validações
      if (!user) {
        throw new Error("Usuário não autenticado");
      }

      if (!selectedDepartment) {
        throw new Error("Selecione uma secretaria");
      }

      if (selectedProducts.length === 0) {
        throw new Error("Adicione pelo menos um produto");
      }

      if (!responsibleName) {
        throw new Error("Informe o nome do responsável");
      }

      if (!selectedConstruction) {
        throw new Error("Informe o nome da obra");
      }

      // 1. Criar o pedido
      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .insert([
          {
            responsible_name: responsibleName,
            department_id: selectedDepartment.id,
            construction_name: selectedConstruction,
            status: 'pending',
            user_id: user.id
          }
        ])
        .select()
        .single();

      if (orderError) {
        console.error('Erro ao criar pedido:', orderError);
        throw orderError;
      }

      // 2. Criar os itens do pedido
      const orderItems = selectedProducts.map(product => ({
        order_id: orderData.id,
        product_id: product.product_id,
        quantity: product.quantity
      }));

      const { error: itemsError } = await supabase
        .from('order_items')
        .insert(orderItems);

      if (itemsError) {
        console.error('Erro ao criar itens:', itemsError);
        // Se houver erro ao criar os itens, excluir o pedido
        await supabase
          .from('orders')
          .delete()
          .eq('id', orderData.id);
        throw itemsError;
      }

      toast({
        title: "Sucesso",
        description: "Pedido criado com sucesso!",
      });

      // Limpar o formulário
      setResponsibleName('');
      setSelectedDepartment(null);
      setSelectedConstruction('');
      setSelectedProducts([]);
      
      // Redirecionar para a página de gerenciamento
      navigate('/orders/management');
    } catch (error: any) {
      console.error('Erro ao criar pedido:', error);
      toast({
        title: "Erro",
        description: error.message || "Ocorreu um erro ao criar o pedido. Por favor, tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="container mx-auto py-8">
      <div className="max-w-4xl mx-auto bg-white p-8 rounded-lg shadow-sm border">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Novo Pedido</h1>
        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="space-y-3">
            <Label className="text-lg font-medium">Secretaria</Label>
            <div className="p-4 bg-gray-50 rounded-lg border">
              {userDepartment ? (
                <span className="text-gray-800 text-lg">
                  {userDepartment.name} {userDepartment.acronym ? `(${userDepartment.acronym})` : ''}
                </span>
              ) : (
                <span className="text-gray-500">Carregando...</span>
              )}
            </div>
          </div>

          {departments[0] && (
            <>
              <div className="space-y-3">
                <Label className="text-lg font-medium">Categoria</Label>
                <Select
                  value={selectedCategory?.toString() || ""}
                  onValueChange={(value) => {
                    setSelectedCategory(Number(value));
                  }}
                  disabled={!departments[0]}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.id.toString()}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label className="text-lg font-medium">Nome da Obra *</Label>
                <Select
                  value={selectedConstruction}
                  onValueChange={setSelectedConstruction}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Selecione uma obra" />
                  </SelectTrigger>
                  <SelectContent>
                    {constructions.map((construction) => (
                      <SelectItem 
                        key={construction.id} 
                        value={construction.name}
                      >
                        {construction.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-4">
                <Label className="text-lg font-medium">Produtos Selecionados</Label>
                <div className="mt-2 space-y-3">
                  {selectedProducts.map((product) => (
                    <div key={product.product_id} className="flex items-center gap-4 p-4 bg-gray-50 border rounded-lg">
                      <span className="flex-grow font-medium">{product.product_name}</span>
                      <Input
                        type="number"
                        min={1}
                        value={product.quantity}
                        onChange={(e) => setSelectedProducts(selectedProducts.map(p => 
                          p.product_id === product.product_id ? { ...p, quantity: parseInt(e.target.value) } : p
                        ))}
                        className="w-32 h-10 text-center"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="default"
                        onClick={() => setSelectedProducts(selectedProducts.filter(p => p.product_id !== product.product_id))}
                        className="px-4"
                      >
                        Remover
                      </Button>
                    </div>
                  ))}
                  {selectedProducts.length === 0 && (
                    <div className="text-gray-500 text-center py-4 bg-gray-50 rounded-lg">
                      Nenhum produto selecionado
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <Label className="text-lg font-medium">Adicionar Produto *</Label>
                <Select
                  value={selectedProduct?.id?.toString() || ""}
                  onValueChange={(value) => {
                    const product = products.find(p => p.id.toString() === value);
                    if (product) {
                      setSelectedProduct(product);
                    }
                  }}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Selecione um produto" />
                  </SelectTrigger>
                  <SelectContent>
                    {products
                      .filter(product => !selectedProducts.some(sp => sp.product_id === product.id))
                      .map((product) => (
                        <SelectItem 
                          key={product.id} 
                          value={product.id.toString()}
                          disabled={product.quantity <= 0}
                          className={product.quantity <= 0 ? 'text-gray-400' : ''}
                        >
                          {product.name}
                          {product.quantity <= 0 ? (
                            <span className="ml-2 text-red-500">(Indisponível)</span>
                          ) : (
                            <span className="ml-2 text-gray-600">(Disponível: {product.quantity})</span>
                          )}
                        </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  min={1}
                  value={quantity}
                  onChange={(e) => handleQuantityChange(e.target.value)}
                  className="w-32 h-10 text-center"
                />
                <Button
                  type="button"
                  variant="primary"
                  size="default"
                  onClick={handleAddProduct}
                  className="px-4"
                >
                  Adicionar
                </Button>
              </div>

              <div className="space-y-3">
                <Label className="text-lg font-medium">Responsável *</Label>
                <Input
                  id="responsible"
                  value={responsibleName}
                  onChange={(e) => setResponsibleName(e.target.value)}
                  placeholder="Nome do responsável pelo pedido"
                  required
                  className="h-12"
                />
              </div>
            </>
          )}
          <div className="flex justify-end gap-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/my-orders')}
              className="h-12 px-6"
            >
              Cancelar
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting}
              variant="primary"
              className="h-12 px-6 font-medium"
            >
              {isSubmitting ? "Criando..." : "Criar Pedido"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateOrder;